import {
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
  ProtocolViewerService,
} from '@barthauer/abc/messages';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { _AbcGridColumn } from '@barthauer/abc/ui-components';
import { ProfileService } from '@barthauer/basys/core/authentication';
import { BasysMapFrameService } from '@barthauer/basys/map-frame';
import { ConfigurationComponent } from './pages/configuration/configuration.component';
import { WizardOptions } from './pages/configuration/WizardOptions';
import { DMLService } from './services/dml.service';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
type MyResultsType = { [key: string]: unknown[] };

@Component({
  selector: 'basys-opera-wizards-create-assets-wizard',
  templateUrl: './create-assets-wizard.component.html',
  styleUrls: ['./create-assets-wizard.component.scss'],
  standalone: false,
})
export class CreateAssetsWizardComponent implements OnInit, OnDestroy {
  public res = ResourceSet.Empty;
  public isReadOnly: boolean;
  buttonStatus = false;
  alive = true;
  display = false;
  firstTitle: string = '';
  textButton: string = '';
  wizardOptions: WizardOptions;
  isLinear = true;
  @ViewChild('container', { static: true, read: ViewContainerRef })
  entry: ViewContainerRef | undefined;
  configurationComponent!: ConfigurationComponent;
  @Output() closeWizardDialog = new EventEmitter();
  @ViewChild('stepAuftrag') stepAuftrag!: MatStep;

  constructor(
    private resolver: ComponentFactoryResolver,
    private dmlService: DMLService,
    private messageBoxService: MessageBoxService,
    private abcCore: AbcCore,
    public frame: BasysMapFrameService,
    private resourceService: ResourceManagerService,
    private protocolViewerService: ProtocolViewerService,
    private profileService: ProfileService,
  ) {
    this.wizardOptions = new WizardOptions();
  }

  get buttonClass() {
    return this.isReadOnly ? 'readonly-button' : '';
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet('opera-wizards');
    this.entry?.clear();
    const factory = this.resolver.resolveComponentFactory(ConfigurationComponent);
    const componentRef = this.entry?.createComponent(factory);
    this.configurationComponent = componentRef?.instance as ConfigurationComponent;
    this.firstTitle = this.res?.get('WizardInformationReady');
    this.textButton = this.res?.get('ButtonTextStartExecution');

    const profileInfo = await this.profileService.getCurrentProfile(true).toPromise();
    this.isReadOnly = ConvertHelper.toBoolean(profileInfo?.settings?.Permissions?.isReadOnly);
  }

  openHelp() {
    this.abcCore.openWindow('http://help.barthauer.de/BaSYS/9.26.1/Web/opera-manager');
  }

  executeDml() {
    if (this.textButton == this.res.get('ButtonTextClose')) {
      this.closeWizardDialog.emit();
    } else {
      this.firstTitle = this.res.get('OperationWillStart');
      this.textButton = this.res.get('ButtonTextClose');
      this.display = !this.display;
      this.buttonStatus = !this.buttonStatus;
      this.wizardOptions.selectedDocument = this.configurationComponent.configId ?? '';
      this.wizardOptions.usingStrang = this.configurationComponent.usingStrang;
      if (this.wizardOptions.selectedDocument) {
        const docId = this.wizardOptions.selectedDocument;

        const parameters: ParameterValue[] = [];
        if (this.wizardOptions.usingStrang) {
          const parameter = new ParameterValue('UseStrang', this.configurationComponent.checked);
          parameters.push(parameter);
        }

        this.dmlService.executeDMLCore(docId, parameters).subscribe({
          next: (results: any) => {
            if (results?.$) {
              Object.keys(results.$).forEach((key) => {
                const category = results.$[key];
                if (key.startsWith('Neue') && category?.Description) {
                  category.Description = this.res.get('ImportAssetsCategoryDescription');
                }
              });
            }

            this.showResults(results);
          },

          error: (error) => this.onExecutionError(error),
        });
      } else {
        this.abcCore.error.publish(new Error(this.res.get('DocumentMustBeSelected')));
      }
    }
  }

  moveStep(stepper: MatStepper): void {
    this.wizardOptions.selectedDocument = this.configurationComponent.configId ?? '';
    this.wizardOptions.usingStrang = this.configurationComponent.usingStrang;
    if (this.wizardOptions.selectedDocument) {
      stepper.next();
    } else {
      const ret = this.res.get('SelectConfiguration');
      this.showMessageAsHtml(ret);
    }
  }

  moveBack(stepper: MatStepper) {
    stepper.previous();
    this.firstTitle = this.res.get('WizardInformationReady');
    this.textButton = this.res.get('ButtonTextStartExecution');
  }

  onStepChange(event: StepperSelectionEvent): void {
    if (event.selectedStep === this.stepAuftrag) {
      this.textButton = this.res.get('ButtonTextStartExecution');
    }
  }

  private onExecutionError(error: any): void {
    this.abcCore.error.publish(error);
  }

  private showResults(results: MyResultsType): void {
    const protocolPopupTitle = this.res.get('AssetsCreationProtocolTitle');

    if (results) {
      this.protocolViewerService.openProtocolViewer(
        results,
        undefined,
        undefined,
        protocolPopupTitle,
        undefined,
        undefined,
      );
    } else {
      const msgOperationCompletedWOAnyChanges = this.res.get('OperationCompletedWithoutCreation');
      this.showMessageAsHtml(msgOperationCompletedWOAnyChanges);
    }

    this.display = false;
    this.firstTitle = this.res.get('OperaFinished');
    this.buttonStatus = false;
  }

  private showMessageAsHtml(htmlMsg: string) {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Info;
    settings.icon = MessageBoxIcon.Info;
    settings.button = MessageBoxButtons.OK;
    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;
    if (this.alive) {
      this.messageBoxService.showMessageBox(settings).subscribe((result: DialogResult) => {});
    }
  }
}
